import { useState, useCallback, useMemo } from "react";
import { Row, Col, useIsMobile } from "../../components/Flexbox";
import FilterBar from "../../components/FilterBar";
import { useTheme } from "../../scripts/functions/ThemeProvider";
import Text from "../../components/Text";
import SelectMonthContainer from "../../components/SelectMonthContainer";
import SelectableContainer from "../../components/SelectableContainer";
import ProgressBar from "../../components/ProgressBar";
import { useRecordNormalizer } from "../../containers/PlantRecordNormalizer";
import RecordMaterialsRow from "../../components/RecordMaterialsRow";
import RecordLineGraph from "../../components/RecordLineGraph";
import Spinner from "../../components/Spinner";
import { useSaveToStorage, loadFromStorage } from "../../utils";
const generateMonths = () => {
    const months = [];
    const currentDate = new Date();
    
    for(let i = 0; i < 6; i++) {
        const date = new Date(currentDate);
        date.setMonth(currentDate.getMonth() - i);

        const name = date.toLocaleDateString('es-MX', {month: 'long', year: 'numeric'});
        let formattedName = name.replace(' de', '');
        months.unshift(formattedName);
    }
    return months;
}

const AVAILABLE_MONTHS = generateMonths();
const HistorialPlanta = () => {
    const persisted = loadFromStorage('PLANT_RECORD_FILTERS') || {};

    const {theme} = useTheme();     
    const [category, setCategory] = useState(persisted.category || {});
    const [percentages, setPercentages] = useState({});
    const [activeContainer, setActiveContainer] = useState(persisted.activeContainer || "Capacidad");
    const [sortConfig, setSortConfig] = useState(persisted.sortConfig || {key: null, direction: 'none'});
    const [selectFilterMonths, setSelectFilterMonths] = useState((persisted.selectFilterMonths && persisted.selectFilterMonths.length > 0)
        ? persisted.selectFilterMonths : [AVAILABLE_MONTHS[0]]
    );
    const {groupedByArea, dataByArea, loading, data} = useRecordNormalizer(category, selectFilterMonths, activeContainer, AVAILABLE_MONTHS);

    const isMobile = useIsMobile();

    useSaveToStorage ('PLANT_RECORD_FILTERS', {category, selectFilterMonths, activeContainer, sortConfig})

    const handleClick = (text) => {
        setActiveContainer((last) => (last === text ? null : text))
    };

    const updateFilterMonths = months => {
        setSelectFilterMonths(months);
    };

    const handleAreaChange = useCallback((nombre, area) => {
        setCategory(prev => ({
            ...prev, [nombre]: area
        }))
    }, []);

    const getPercentages = useCallback((name, percentage) => {
        setPercentages(prev => {
            if (prev[name] === percentage) {
                return prev;
            }

            return {...prev, [name]: percentage};
        });
    }, []);

    //Sort the graphs in a descending order
    const sortGroups = useMemo(() => {
        const sortArray = (arr) => {
            return [...arr].sort((a, b) => {
                const A = percentages[a.nombre] || 0;
                const B = percentages[b.nombre] || 0;
                return B - A;
            });
        };

        return {
            FLEXO: sortArray(groupedByArea.FLEXO),
            OFFSET: sortArray(groupedByArea.OFFSET),
            OTROS: sortArray(groupedByArea.OTROS),
        }
    }, [groupedByArea, percentages]);

    const sortLineGroups = useMemo(() => {
        const sortArray = (arr) => {
            return [...arr].sort((a, b) => {
                const A = percentages[a.nombre] || 0;
                const B = percentages[b.nombre] || 0;
                return B - A;
            });
        };

        return {
            FLEXO: sortArray(dataByArea.FLEXO),
            OFFSET: sortArray(dataByArea.OFFSET),
            OTROS: sortArray(dataByArea.OTROS),
        }
    }, [dataByArea, percentages]);

    const handleSort = (columnKey) => {
        setSortConfig(lastSort => {
            let direction = 'asc';
            let key = columnKey;
            
            if (lastSort.key === columnKey) {
                if (lastSort.direction === 'asc') {
                    direction = 'desc';
                } else if (lastSort.direction === 'desc') {
                    direction = 'none';
                    key = null;
                }
            }
            return {key, direction};
        });
    }

    const sortedData = useMemo(() => {
        if (!data || !Array.isArray(data)) {
            return [];
        }

        const {key, direction} = sortConfig;
        if (direction === 'none' || !key) {
            return data;
        }

        const toSort = [...data];
        toSort.sort((a, b) => {
            let valA = a[key];
            let valB = b[key];

            //Sort the numeric column (Cantidad)
            if (key === 'cantidad') {
                valA = parseFloat(valA) || 0;
                valB = parseFloat(valB) || 0;

                if (valA < valB) {
                    return direction === 'asc' ? -1 : 1;
                }
                if (valA > valB) {
                    return direction === 'asc' ? 1 : -1;
                }
                return 0;
            }

            //Sort every other column
            valA = String(valA).toLowerCase();
            valB = String(valB).toLowerCase();

            const comparison = valA.localeCompare(valB, 'es', {sensivity: 'base'});
            return direction === 'asc' ? comparison : -comparison;
        });
        return toSort;
    }, [data, sortConfig]);

    const getIndicator = (columnKey) => {
        if (sortConfig.key !== columnKey) return null;
        if (sortConfig.direction === 'asc') return "▲";
        if (sortConfig.direction === 'desc') return "▼";
        return null;
    }

    return (
        <Col style={{width: '100%', justifyContent: 'flex-start', flex: 1, paddingLeft: 64, paddingRight: 64, marginTop: '32px', overflow: 'hidden', boxSizing: 'border-box'}}>
            <FilterBar style={{width: '100%', maxHeight: '60px', justifyContent: 'space-between', alignItems: 'center', flex: 'none'}}>
                <Row style={{width: '100%', maxHeight: '60px', wrap: 'nowrap', paddingLeft: '2px'}}>
                    <SelectMonthContainer style={{flexWrap: 'nowrap', width: '100%'}} showYear={false} selectedMonths={selectFilterMonths} updateMonths={updateFilterMonths} detectedMonths={AVAILABLE_MONTHS} theme={theme}/>
                </Row>
                <Row style={{justifyContent: 'flex-end', maxWidth: 300, height: '60px', alignItems: 'end'}}>
                    <SelectableContainer theme={theme} text="Capacidad" active = {activeContainer === "Capacidad"} onClick = {() => handleClick("Capacidad")}></SelectableContainer>
                    <SelectableContainer theme={theme} text="Materiales" active = {activeContainer === "Materiales"} onClick = {() => handleClick("Materiales")}></SelectableContainer>
                </Row>
            </FilterBar>
            {loading ? (
                <Col style={{justifyContent: 'center', alignItems: 'center', minHeight: 500, width:'100%'}}>
                    <Spinner theme={theme}/>
                </Col>
            ) : (
                <>
                    {activeContainer === "Materiales" && (
                        <Col style={{width: '100%', alignItems: 'flex-start', justifyContent: 'flex-start', marginTop: 20, flex: 1, overflow: 'hidden', minHeight: 0}}>
                            <div style={{width: '100%', flex: 1, minHeight: 0, overflowX: isMobile ? 'auto' : 'hidden'}}>
                                <Col style={{width: '100%', minWidth: isMobile ? 1008 : undefined, height: '100%', alignItems: 'flex-start', minHeight: 0}}>
                                    <div style={{width: '100%', flex: 1, overflowY: 'auto', minHeight: 0}}>
                                        <div style={{position: 'sticky', top: 0, zIndex: 1, display: 'flex', justifyContent: 'space-between', width: '100%', height: '38px', alignItems: 'center', boxSizing: 'border-box', backgroundColor: theme.White !== "#FFFFFF" ? theme.Ey : theme.Ed, border: `0.5px solid ${theme.Ey}`, borderRadius: '4px'}}>
                                            {[['material', 'Material', 447], ['partes', 'No. Parte', 152], ['cliente', 'Cliente', 152], ['cantidad', 'Cantidad utilizada', 156], ['unidad', 'Unidad', 101]].map(([key, label, width]) => (
                                                <div key={key} onClick={() => handleSort(key)} style={{display: 'flex', alignItems: 'center', justifyContent: ['cantidad', 'unidad'].includes(key) ? 'flex-end' : 'flex-start', boxSizing: 'border-box', padding: '0 16px', width, flex: 'none', height: '36px', cursor: 'pointer', userSelect: 'none'}}>
                                                    <Text size={15} weight="medium" color={theme.ONED}>{label}</Text>
                                                    <Text size={12} style={{marginLeft: '4px'}}>{getIndicator(key)}</Text>
                                                </div>
                                            ))}
                                        </div>
                                        {sortedData.map(item => (
                                            <RecordMaterialsRow selectedMonths={selectFilterMonths} theme={theme} key={item.id} data={item}/>
                                        ))}
                                    </div>
                                </Col>
                            </div>
                        </Col>
                    )}
                    {activeContainer === "Capacidad" && (
                        <Col style={{ width: '100%', flex: 1, overflowY: 'auto', justifyContent: 'start', minHeight: 0}}>
                            <div style={{width: '100%', paddingTop: 40}}>
                                <Row style={{ justifyContent: 'space-between', width: '100%', alignItems: 'flex-start' }}>
                                    {selectFilterMonths.length < 2 ? (
                                        <>
                                            <Col style={{ flex: 1, alignItems: 'start', gap: '10px'}}>
                                                <Text weight="bold" size={15} color={theme.ONED} style={{paddingBottom: 15}}>FLEXO</Text>
                                                {sortGroups.FLEXO.map((item) => (
                                                    <ProgressBar theme={theme} key={item.key} data={item} months={selectFilterMonths} onAreaChange={handleAreaChange} onPercentageChange={getPercentages} selectedArea={category[item.nombre] || item.area}/>
                                                ))}
                                            </Col>
                                            <Col style={{ flex: 1, alignItems: 'start', gap: '10px'}}>
                                                <Text weight="bold" size={15} color={theme.ONED} style={{paddingBottom: 15}}>OFFSET</Text>
                                                {sortGroups.OFFSET.map((item) => (
                                                    <ProgressBar theme={theme} key={item.key} data={item} months={selectFilterMonths} onAreaChange={handleAreaChange} onPercentageChange={getPercentages} selectedArea={category[item.nombre] || item.area}/>
                                                ))}
                                            </Col>
                                            <Col style={{ flex: 1, alignItems: 'start', gap: '10px'}}>
                                                <Text weight="bold" size={15} color={theme.ONED} style={{paddingBottom: 15}}>OTROS</Text>
                                                {sortGroups.OTROS.map((item) => (
                                                    <ProgressBar theme={theme} key={item.key} data={item} months={selectFilterMonths} onAreaChange={handleAreaChange} onPercentageChange={getPercentages} selectedArea={category[item.nombre] || item.area}/>
                                                ))}
                                            </Col>
                                        </>
                                    ) : (
                                        <>
                                            <Col style={{ flex: 1, alignItems: 'start', gap: '10px'}}>
                                                <Text weight="bold" size={15} color={theme.ONED} style={{paddingBottom: 15}}>FLEXO</Text>
                                                {sortLineGroups.FLEXO.map((item) => (
                                                    <RecordLineGraph theme={theme} key={item.key} data={item} months={selectFilterMonths} onAreaChange={handleAreaChange} onPercentageChange={getPercentages} selectedArea={category[item.nombre] || item.area}/>
                                                ))}
                                            </Col>
                                            <Col style={{ flex: 1, alignItems: 'start', gap: '10px'}}>
                                                <Text weight="bold" size={15} color={theme.ONED} style={{paddingBottom: 15}}>OFFSET</Text>
                                                {sortLineGroups.OFFSET.map((item) => (
                                                    <RecordLineGraph theme={theme} key={item.key} data={item} months={selectFilterMonths} onAreaChange={handleAreaChange} onPercentageChange={getPercentages} selectedArea={category[item.nombre] || item.area}/>
                                                ))}
                                            </Col>
                                            <Col style={{ flex: 1, alignItems: 'start', gap: '10px'}}>
                                                <Text weight="bold" size={15} color={theme.ONED} style={{paddingBottom: 15}}>OTROS</Text>
                                                {sortLineGroups.OTROS.map((item) => (
                                                    <RecordLineGraph theme={theme} key={item.key} data={item} months={selectFilterMonths} onAreaChange={handleAreaChange} onPercentageChange={getPercentages} selectedArea={category[item.nombre] || item.area}/>
                                                ))}
                                            </Col>
                                        </>
                                    )}
                                    
                                </Row>
                            </div>
                        </Col>
                    )}
                </>
            )}
        </Col>
    )
}

export default HistorialPlanta;